import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {DepartmentDto, ProvinceDto, DistrictDto, SubDistrictDto} from './department-service';
import {compareByNumericValue} from '../../../shared/commons/select-compare.util';
import {resolveRecordedBy, formatRecordedAt} from '../../../shared/commons/audit-display.util';

@Component({
  selector: 'app-department-modal',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './department-modal.html',
  styleUrl: './department-modal.scss'
})
export class DepartmentModal implements OnInit {
  @Input() item: DepartmentDto | undefined;
  @Input() viewOnly = false;
  @Input() onSave?: (payload: any, isEditMode: boolean) => Promise<boolean>;

  form: FormGroup;
  isEditMode = false;
  submitted = false;
  isSubmitting = false;
  recordedByDisplay = '-';
  recordedAtDisplay = '-';
  isProvinceLoading = false;
  isDistrictLoading = false;
  isSubdistrictLoading = false;
  readonly compareByNumericValue = compareByNumericValue;

  provinces: ProvinceDto[] = [];
  districts: DistrictDto[] = [];
  subdistricts: SubDistrictDto[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      id: [null],
      departmentCode: ['', [Validators.required, Validators.maxLength(255)]],
      nameTh: ['', [Validators.required, Validators.maxLength(255)]],
      nameEn: ['', [Validators.maxLength(255)]],
      taxid: ['', [Validators.required, Validators.maxLength(13)]],
      address: [''],
      province: [null, Validators.required],
      district: [{value: null, disabled: true}, Validators.required],
      subdistrict: [{value: null, disabled: true}, Validators.required],
      zipcode: ['', [Validators.required, Validators.maxLength(5)]],
      telephone: ['', [Validators.maxLength(255)]],
      fax: ['', [Validators.maxLength(255)]],
      lat: [''],
      lng: [''],
      ordering: [0],
      state: [1]
    });
  }

  ngOnInit(): void {
    this.loadProvinces();

    this.form.get('province')?.valueChanges.subscribe(provinceId => {
      this.onProvinceChange(provinceId);
    });

    this.form.get('district')?.valueChanges.subscribe(districtId => {
      this.onDistrictChange(districtId);
    });

    if (this.item) {
      this.isEditMode = true;
      this.recordedByDisplay = resolveRecordedBy(this.item);
      this.recordedAtDisplay = formatRecordedAt(this.item);
      this.form.patchValue({
        id: this.item.id,
        departmentCode: this.item.departmentCode,
        nameTh: this.item.nameTh,
        nameEn: this.item.nameEn,
        taxid: this.item.taxid,
        address: this.item.address,
        province: this.item.province,
        zipcode: this.item.zipcode,
        telephone: this.item.telephone,
        fax: this.item.fax,
        lat: this.item.lat,
        lng: this.item.lng,
        ordering: this.item.ordering,
        state: this.item.state
      });
      if (this.item.province) {
        this.loadDistrictsForProvince(this.item.province, this.viewOnly, () => {
          this.form.get('district')?.patchValue(this.item!.district, {emitEvent: false});
          if (this.item!.district) {
            this.loadSubdistrictsForDistrict(this.item!.district, this.viewOnly, () => {
              this.form.get('subdistrict')?.patchValue(this.item!.subdistrict, {emitEvent: false});
            });
          }
        });
      }
    } else {
      this.form.reset({ordering: 0, state: 1});
      this.recordedByDisplay = '-';
      this.recordedAtDisplay = '-';
    }

    if (this.viewOnly) {
      this.form.disable();
    }

    this.cdr.detectChanges();
  }

  private loadProvinces(): void {
    this.isProvinceLoading = true;
    this.http.get<ProvinceDto[]>('/api/master/provinces/active').subscribe({
      next: provinces => {
        this.provinces = provinces;
        this.isProvinceLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isProvinceLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private onProvinceChange(provinceId: number | null): void {
    this.districts = [];
    this.subdistricts = [];
    this.form.get('district')?.patchValue(null, {emitEvent: false});
    this.form.get('subdistrict')?.patchValue(null, {emitEvent: false});
    if (provinceId) {
      this.loadDistrictsForProvince(provinceId, this.viewOnly);
    } else {
      this.form.get('district')?.disable();
      this.form.get('subdistrict')?.disable();
    }
  }

  private onDistrictChange(districtId: number | null): void {
    this.subdistricts = [];
    this.form.get('subdistrict')?.patchValue(null, {emitEvent: false});
    if (districtId) {
      this.loadSubdistrictsForDistrict(districtId, this.viewOnly);
    } else {
      this.form.get('subdistrict')?.disable();
    }
  }

  private loadDistrictsForProvince(provinceId: number, isViewOnly: boolean, callback?: () => void): void {
    this.isDistrictLoading = true;
    this.http.get<DistrictDto[]>(`/api/master/districts/by-province/${provinceId}`).subscribe({
      next: districts => {
        this.districts = districts;
        this.isDistrictLoading = false;
        if (!isViewOnly) {
          this.form.get('district')?.enable();
        }
        if (callback) {
          callback();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isDistrictLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadSubdistrictsForDistrict(districtId: number, isViewOnly: boolean, callback?: () => void): void {
    this.isSubdistrictLoading = true;
    this.http.get<SubDistrictDto[]>(`/api/master/subdistricts/by-district/${districtId}`).subscribe({
      next: subdistricts => {
        this.subdistricts = subdistricts;
        this.isSubdistrictLoading = false;
        if (!isViewOnly) {
          this.form.get('subdistrict')?.enable();
        }
        if (callback) {
          callback();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSubdistrictLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onTaxidKeyup(): void {
    const taxid = this.form.get('taxid')?.value;
    if (taxid && taxid.length === 13) {
      const excludeId = this.form.get('id')?.value || undefined;
      this.http.get<boolean>(`/api/master/departments/check-taxid/${taxid}${excludeId ? `?excludeId=${excludeId}` : ''}`)
        .subscribe(exists => {
          if (exists) {
            alert('เลขผู้เสียภาษีนี้ ได้ถูกลงทะเบียนแล้ว');
            this.form.get('taxid')?.setValue('');
          }
        });
    }
  }

  save(): void {
    if (this.viewOnly) {
      return;
    }
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    const rawValue = this.form.getRawValue();
    rawValue.state = rawValue.state ? 1 : 0;
    void this.submitAndClose(rawValue);
  }

  private async submitAndClose(payload: any): Promise<void> {
    if (!this.onSave) {
      this.activeModal.close(payload);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const isSuccess = await this.onSave(payload, this.isEditMode);
      if (isSuccess) {
        this.activeModal.close(payload);
      }
    } finally {
      this.isSubmitting = false;
    }
  }
}
